import React from 'react';
import { Bell, CheckCircle2, Minus, Plus } from 'lucide-react';

/**
 * HomeRecentAlerts — compact dark strip summarising at-risk priorities
 * and any chain stage below the alert threshold. Shows an all-clear
 * chip when nothing is flagged. Admins can tune the threshold inline.
 */
export default function HomeRecentAlerts({ stages, atRisk, threshold = 80, canEdit = false, onThresholdChange }) {
  const alerts = (stages || []).filter((s) => s.score > 0 && s.score < threshold);
  return (
    <div className="rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl px-5 py-4 flex items-center gap-4 overflow-x-auto" data-testid="home-recent-alerts">
      <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400 shrink-0">
        <Bell className="h-3.5 w-3.5 text-[#44b6d2]" /> Recent Alerts
      </span>
      {alerts.length === 0 && atRisk === 0 ? (
        <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400 font-medium" data-testid="home-alert-all-clear">
          <CheckCircle2 className="h-4 w-4" /> All systems healthy — no active alerts.
        </span>
      ) : (
        <div className="flex items-center gap-2.5 flex-nowrap">
          {atRisk > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 px-3 py-1 text-xs font-semibold text-amber-300 whitespace-nowrap" data-testid="home-alert-at-risk">
              {atRisk} priorit{atRisk === 1 ? 'y' : 'ies'} at risk
            </span>
          )}
          {alerts.map((s) => {
            const tone = s.score >= 50 ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-300';
            return (
              <span key={s.label} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold whitespace-nowrap ${tone}`} data-testid={`home-alert-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                {s.label} {Math.round(s.score)}%
              </span>
            );
          })}
        </div>
      )}
      {canEdit && (
        <span className="ml-auto flex items-center gap-1.5 shrink-0 pl-3 border-l border-[#1f2a44]" data-testid="home-alert-threshold-control">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Alert below</span>
          <button
            type="button"
            onClick={() => onThresholdChange?.(threshold - 5)}
            className="h-6 w-6 rounded-md bg-[#0b1220] ring-1 ring-[#22304d] text-slate-300 hover:text-white flex items-center justify-center"
            data-testid="home-alert-threshold-down"
            aria-label="Lower alert threshold"
          >
            <Minus className="h-3 w-3" />
          </button>
          <span className="text-sm font-bold text-slate-100 tabular-nums w-9 text-center" data-testid="home-alert-threshold-value">{threshold}%</span>
          <button
            type="button"
            onClick={() => onThresholdChange?.(threshold + 5)}
            className="h-6 w-6 rounded-md bg-[#0b1220] ring-1 ring-[#22304d] text-slate-300 hover:text-white flex items-center justify-center"
            data-testid="home-alert-threshold-up"
            aria-label="Raise alert threshold"
          >
            <Plus className="h-3 w-3" />
          </button>
        </span>
      )}
    </div>
  );
}
